import IconButtonTsoPq from '@components/Buttons/IconButtonTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { GridColDef } from '@mui/x-data-grid-pro';
import {
  useGetTransducerModelByIdHasDataQuery,
  useGetTransducerModelByIdIsReferencedQuery,
  useGetTransducerModelGetAllQuery,
} from '@redux/apiSlices/transducerModelApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GenericGridPage from '../GenericGridPageProps';
import { StyledEditIconWrapper } from '../StyledGridPageComponents';
import TransducerModal from './TransducerModal';

export type RowDataTransducer = {
  id: number;
  manufacturer: string;
  model: string;
  type: number;
  voltageFrequencyDeviationMagnitude1: NullableValues;
  voltageFrequencyDeviationMagnitude10: NullableValues;
  voltageFrequencyDeviationPhaseAngle1: NullableValues;
  voltageFrequencyDeviationPhaseAngle10: NullableValues;
  currentFrequencyDeviationMagnitude1: NullableValues;
  currentFrequencyDeviationMagnitude10: NullableValues;
  currentFrequencyDeviationPhaseAngle1: NullableValues;
  currentFrequencyDeviationPhaseAngle10: NullableValues;
  transformerRatioVoltage: string | null | undefined;
  transformerRatioCurrent: string | null | undefined;
  comment: string;
};

export type NullableValues = number | null | undefined | string;

const TransducerModel = () => {
  const { t } = useTranslation();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [addNew, setAddNew] = useState<boolean>(false);
  const [selected, setSelected] = useState<RowDataTransducer | null>(null);

  const { data: rows } = useGetTransducerModelGetAllQuery();
  const { data: hasData } = useGetTransducerModelByIdHasDataQuery(selected?.id ?? skipToken, { skip: !selected });
  const { data: isReferenced } = useGetTransducerModelByIdIsReferencedQuery(selected?.id ?? skipToken, {
    skip: !selected,
  });

  const getType = (enumValue: number) => {
    if (enumValue === 0) return t('masterdata.transducer.table.transducerModel.voltage');
    if (enumValue === 1) return t('masterdata.transducer.table.transducerModel.current');
    if (enumValue === 2) return t('masterdata.transducer.table.transducerModel.combined');
  };

  const formatTransmissionRatio = (primary: number | null | undefined, secondary: number | null | undefined) => {
    if (primary && secondary) return `${primary}:${secondary}`;
    if (primary && !secondary) return `${primary}:00`;
    if (!primary && secondary) return `000:${secondary}`;
    return null;
  };

  const transformedRows: RowDataTransducer[] = (rows?.externalDtos ?? []).map((item) => ({
    id: item.id ?? 0,
    manufacturer: item.manufacturer,
    model: item.model,
    type: item.type ?? 0,
    transformerRatioVoltage: formatTransmissionRatio(item.voltageRatioPrimary, item.voltageRatioSecondary) ?? null,
    transformerRatioCurrent: formatTransmissionRatio(item.currentRatioPrimary, item.currentRatioSecondary) ?? null,
    voltageFrequencyDeviationMagnitude1: item.voltageFrequencyDeviationMagnitude1 ?? null,
    voltageFrequencyDeviationMagnitude10: item.voltageFrequencyDeviationMagnitude10 ?? null,
    voltageFrequencyDeviationPhaseAngle1: item.voltageFrequencyDeviationPhaseAngle1 ?? null,
    voltageFrequencyDeviationPhaseAngle10: item.voltageFrequencyDeviationPhaseAngle10 ?? null,
    currentFrequencyDeviationMagnitude1: item.currentFrequencyDeviationMagnitude1 ?? null,
    currentFrequencyDeviationMagnitude10: item.currentFrequencyDeviationMagnitude10 ?? null,
    currentFrequencyDeviationPhaseAngle1: item.currentFrequencyDeviationPhaseAngle1 ?? null,
    currentFrequencyDeviationPhaseAngle10: item.currentFrequencyDeviationPhaseAngle10 ?? null,
    comment: item.comment,
  }));

  const commonColumnProps = {
    headerClassName: 'customBackgroundHeader',
    resizable: true,
  };

  const createDevitationColumns = (prefix: 'voltage' | 'current'): GridColDef[] => [
    {
      field: `${prefix}FrequencyDeviationPhaseAngle10`,
      headerName:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t(`masterdata.transducer.table.frequencyDeviationPhaseAngle10`),
      width: 100,
      ...commonColumnProps,
    },
    {
      field: `${prefix}FrequencyDeviationPhaseAngle1`,
      headerName:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t(`masterdata.transducer.table.frequencyDeviationPhaseAngle1`),
      width: 100,
      ...commonColumnProps,
    },
    {
      field: `${prefix}FrequencyDeviationMagnitude10`,
      headerName:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t(`masterdata.transducer.table.frequencyDeviationMagnitude10`),
      width: 100,
      ...commonColumnProps,
    },
    {
      field: `${prefix}FrequencyDeviationMagnitude1`,
      headerName:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t(`masterdata.transducer.table.frequencyDeviationMagnitude1`),
      width: 100,
      ...commonColumnProps,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: 'manufacturer',
      headerName: t('masterdata.transducer.table.manufacturer'),
      width: 100,
      ...commonColumnProps,
    },
    {
      field: 'model',
      headerName: t('masterdata.transducer.table.model'),
      width: 100,
      ...commonColumnProps,
    },
    {
      field: 'type',
      headerName: t('masterdata.transducer.table.transducerModel'),
      width: 100,
      ...commonColumnProps,

      valueGetter: (value) => getType(value),
    },
    {
      field: 'transformerRatioVoltage',
      headerName: t('masterdata.transducer.table.transmissionRatioVoltage'),
      width: 100,
      ...commonColumnProps,
    },
    ...createDevitationColumns('voltage'),
    {
      field: 'transformerRatioCurrent',
      headerName: t('masterdata.transducer.table.transmissionRatioCurrent'),
      width: 100,
      ...commonColumnProps,
    },
    ...createDevitationColumns('current'),
    {
      field: 'comment',
      headerName: t('management.substation.comment'),
      flex: 1,
      width: 100,
      ...commonColumnProps,
    },
    {
      field: 'edit',
      headerName: '',
      width: 50,
      headerClassName: 'customBackgroundHeader',
      sortable: false,
      filterable: false,
      resizable: true,
      renderCell: (params) => (
        <PermissionGate roles={userOrSupportRestriction}>
          <StyledEditIconWrapper>
            <IconButtonTsoPq
              sizeVariant="s"
              buttonType="inputIconPrimary"
              icon={faPen}
              onClick={(event) => {
                event.stopPropagation();
                setShowModal(true);
                setSelected(params.row);
              }}
            />
          </StyledEditIconWrapper>
        </PermissionGate>
      ),
    },
  ];

  const modalComponent =
    (showModal && (
      <ModalDialogTsoPq modalState={showModal} customMaxWidth="40.325rem">
        <TransducerModal
          customMaxWidth="40.325rem"
          newTransducerModelType={addNew}
          handleDiscard={() => {
            setShowModal(false);
            setAddNew(false);
            setSelected(null);
          }}
          updateDisabled={hasData}
          deleteDisabled={isReferenced}
          selectedTransducer={selected || undefined}
          defaultValues={!addNew ? (selected ?? {}) : undefined}
        />
      </ModalDialogTsoPq>
    )) ||
    null;

  return (
    <GenericGridPage<RowDataTransducer>
      title={`${t('masterdata.transducer.section')}`}
      rows={transformedRows ?? []}
      columns={columns}
      onAddClick={() => {
        setAddNew(true);
        setShowModal(true);
      }}
      modalComponent={modalComponent}
      searchLabel={t('searchfield.search')}
      sortModel={[
        { field: 'manufacturer', sort: 'asc' },
        { field: 'model', sort: 'asc' },
      ]}
    />
  );
};

export default TransducerModel;
